import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../cn";
import { Spinner } from "./Spinner";

export type ButtonVariant =
    | "primary" // brand gradient (orange-500 → amber-500) + glow
    | "secondary" // glass outline
    | "ghost" // text only
    | "dark" // black/20 surface (select trigger look)
    | "danger" // red outline
    | "outline"; // white/10 border, white/5 bg

export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Rounded-full pill instead of rounded-xl */
    pill?: boolean;
    /** Uppercase + tracking-widest (admin-style CTA) */
    uppercase?: boolean;
    /** Show a loading spinner instead of children */
    loading?: boolean;
}

/* Classes copied 1:1 from discordforge.org components:
 * - primary:  bg-gradient-to-r from-orange-500 to-amber-500 … (AdminApprovalQueue.tsx,
 *             AdminQueueCard.tsx, StepBotInvite.tsx)
 * - secondary: border border-white/10 bg-white/5 hover:bg-white/10 (TeamModal.tsx close,
 *             Header.tsx, etc.)
 * - dark:     border border-white/10 bg-black/20 (CustomSelect.tsx trigger)
 * - danger:   red-500/10 surface + red-400 text (ReportModal.tsx, DeleteAnnouncementModal.tsx)
 */
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
    primary:
        "bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold " +
        "shadow-[0_0_30px_rgba(249,115,22,0.2)] " +
        "hover:from-orange-600 hover:to-amber-600 hover:shadow-[0_0_30px_rgba(249,115,22,0.3)]",
    secondary:
        "border border-white/10 bg-white/5 text-white font-semibold " +
        "hover:bg-white/10 hover:border-white/20",
    ghost:
        "text-zinc-300 font-semibold hover:text-white hover:bg-white/5",
    dark:
        "border border-white/10 bg-black/20 text-white font-semibold hover:bg-white/10",
    danger:
        "border border-red-500/30 bg-red-500/10 text-red-400 font-semibold hover:bg-red-500/20 hover:text-red-300",
    outline:
        "border border-white/15 bg-transparent text-white font-semibold hover:bg-white/5 hover:border-white/25",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
    sm: "px-3 py-1.5 text-xs gap-1.5",
    md: "px-4 py-2.5 text-sm gap-2",
    lg: "px-6 py-3 text-sm gap-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant = "primary",
            size = "md",
            pill = false,
            uppercase = false,
            loading = false,
            className,
            disabled,
            children,
            type = "button",
            ...rest
        },
        ref
    ) => (
        <button
            ref={ref}
            type={type}
            disabled={disabled || loading}
            className={cn(
                "inline-flex cursor-pointer items-center justify-center outline-none transition-all duration-200",
                "active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50",
                "focus-visible:ring-2 focus-visible:ring-orange-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black",
                VARIANT_CLASSES[variant],
                SIZE_CLASSES[size],
                pill ? "rounded-full" : "rounded-xl",
                uppercase && "uppercase tracking-widest",
                className
            )}
            {...rest}
        >
            {loading && <Spinner size={size === "lg" ? 18 : 14} />}
            {children}
        </button>
    )
);

Button.displayName = "Button";
